import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import ProjectCard from './ProjectCard';
import Button from '../../../components/ui/Button';

const ProjectCarousel = ({ projects, status, title, icon, color }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 2;
  
  const statusProjects = projects?.filter(project => 
    status === 'active' ? project?.status === 'approved' : project?.status === status
  ) || [];

  const totalPages = Math.ceil(statusProjects?.length / itemsPerPage);
  const canScrollLeft = currentIndex > 0;
  const canScrollRight = currentIndex < totalPages - 1;

  const scrollLeft = () => {
    if (canScrollLeft) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const scrollRight = () => {
    if (canScrollRight) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const getCurrentProjects = () => {
    const start = currentIndex * itemsPerPage;
    const end = start + itemsPerPage;
    return statusProjects?.slice(start, end);
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 'active':
        return 'Projects that are approved and ready for production';
      case 'pending':
        return 'Projects awaiting client review and approval';
      case 'in-review':
        return 'Projects currently under design review';
      case 'rejected':
        return 'Projects that need revisions or redesign';
      default:
        return 'Project status overview';
    }
  };

  const getStatusColorScheme = (status) => {
    switch (status) {
      case 'active':
        return {
          bg: 'from-success/5 to-success/10',
          border: 'border-success/20',
          text: 'text-success',
          badge: 'bg-success/10 border-success/30 text-success'
        };
      case 'pending':
        return {
          bg: 'from-warning/5 to-warning/10',
          border: 'border-warning/20',
          text: 'text-warning',
          badge: 'bg-warning/10 border-warning/30 text-warning'
        };
      case 'in-review':
        return {
          bg: 'from-accent/5 to-accent/10',
          border: 'border-accent/20',
          text: 'text-accent',
          badge: 'bg-accent/10 border-accent/30 text-accent'
        };
      case 'rejected':
        return {
          bg: 'from-error/5 to-error/10',
          border: 'border-error/20',
          text: 'text-error',
          badge: 'bg-error/10 border-error/30 text-error'
        };
      default:
        return {
          bg: 'from-muted/5 to-muted/10',
          border: 'border-muted/20',
          text: 'text-muted-foreground',
          badge: 'bg-muted/10 border-muted/30 text-muted-foreground'
        };
    }
  };

  const colorScheme = getStatusColorScheme(status);

  if (statusProjects?.length === 0) {
    return (
      <div className={`bg-gradient-to-br ${colorScheme?.bg} border-2 ${colorScheme?.border} rounded-xl p-6 shadow-lg`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-lg font-heading ${colorScheme?.text} flex items-center gap-2`}>
              <Icon name={icon} size={20} />
              {title}
            </h3>
            <p className="text-sm text-foreground/70">{getStatusDescription(status)}</p>
          </div>
          <div className={`${colorScheme?.badge} border rounded-lg px-3 py-1`}>
            <span className="text-sm font-medium">0 projects</span>
          </div>
        </div>
        <div className="bg-card/60 border-2 border-dashed border-border/60 rounded-lg p-8 text-center">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name={icon} size={24} className="text-muted-foreground" />
          </div>
          <h4 className="text-md font-heading text-foreground mb-1">No {title}</h4>
          <p className="text-sm text-foreground/70">
            {status === 'active' ? 'Projects will appear here once approved' : 
             status === 'pending' ? 'New projects will appear here for review' :
             status === 'in-review'? 'Projects under review will appear here' : 'Rejected projects will appear here for revision'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br ${colorScheme?.bg} border-2 ${colorScheme?.border} rounded-xl p-6 shadow-lg`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`text-lg font-heading ${colorScheme?.text} flex items-center gap-2`}>
            <Icon name={icon} size={20} />
            {title}
          </h3>
          <p className="text-sm text-foreground/70">{getStatusDescription(status)}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`${colorScheme?.badge} border rounded-lg px-3 py-1`}>
            <span className="text-sm font-medium">
              {statusProjects?.length} project{statusProjects?.length !== 1 ? 's' : ''}
            </span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                iconName="ChevronLeft"
                onClick={scrollLeft}
                disabled={!canScrollLeft}
                className={`w-8 h-8 p-0 ${!canScrollLeft ? 'opacity-30 cursor-not-allowed' : 'hover:bg-card/50'}`}
              />
              <span className="text-xs text-foreground/65 px-2">
                {currentIndex + 1} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                iconName="ChevronRight"
                onClick={scrollRight}
                disabled={!canScrollRight}
                className={`w-8 h-8 p-0 ${!canScrollRight ? 'opacity-30 cursor-not-allowed' : 'hover:bg-card/50'}`}
              />
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 min-h-[280px]">
        {getCurrentProjects()?.map((project) => (
          <ProjectCard key={project?.id} project={project} />
        ))}
        
        {/* Fill empty slots to maintain consistent height */}
        {getCurrentProjects()?.length < itemsPerPage && (
          Array.from({ length: itemsPerPage - getCurrentProjects()?.length })?.map((_, index) => (
            <div key={`empty-${index}`} className="invisible">
              <div className="bg-card border border-border rounded-lg p-6 h-full"></div>
            </div>
          ))
        )}
      </div>
      {/* Pagination dots for better UX */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 gap-2">
          {Array.from({ length: totalPages })?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentIndex 
                  ? `${colorScheme?.text?.replace('text-', 'bg-')}` 
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectCarousel;